import { effectivePollStatus, formatSlot, zonedToUtc, type PollListEntry } from '@kartothek/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useCampaign, useCampaignId } from '../hooks';
import { t, tDyn } from '../i18n';

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function StatusBadge({ p }: { p: { status: PollListEntry['status']; deadline: string | null } }) {
  const s = effectivePollStatus(p);
  return <span className={`poll-status poll-status-${s}`}>{tDyn(`polls.status.${s}`)}</span>;
}

/** Übersicht der Terminumfragen; DM/Co-DM legen hier neue an. */
export function PollsPage() {
  const cid = useCampaignId();
  const campaign = useCampaign(cid);
  const gm = !!campaign.data && campaign.data.role !== 'player';
  const polls = useQuery({ queryKey: ['polls', cid], queryFn: () => api.polls(cid) });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    document.title = `${t('polls.title')} · Kartothek`;
  }, []);

  return (
    <div className="page-narrow polls-page">
      <div className="page-head">
        <h1>{t('polls.title')}</h1>
        {gm && !creating && (
          <button className="primary" onClick={() => setCreating(true)}>
            + {t('polls.new')}
          </button>
        )}
      </div>
      <p className="muted">{gm ? t('polls.intro') : t('polls.playerIntro')}</p>
      {gm && creating && <CreatePollForm cid={cid} onCancel={() => setCreating(false)} />}
      {polls.data?.length === 0 && <p className="muted">{t('polls.none')}</p>}
      <ul className="poll-list">
        {(polls.data ?? []).map((p) => (
          <li key={p.id} className="card poll-entry">
            <Link to={`/c/${cid}/polls/${p.id}`} className="poll-entry-title">
              {p.title}
            </Link>
            <StatusBadge p={p} />
            <div className="small muted">
              {p.final
                ? t('polls.fixedOn', { slot: formatSlot(p.final.startAt, p.final.endAt) })
                : [
                    t('polls.slotCount', { n: p.slotCount }),
                    t('polls.answered', { n: p.answeredCount, total: p.participantCount }),
                    p.deadline ? t('polls.until', { date: formatSlot(p.deadline) }) : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CreatePollForm({ cid, onCancel }: { cid: string; onCancel: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const auth = useQuery({ queryKey: ['auth'], queryFn: api.authState, staleTime: Infinity });
  const members = useQuery({ queryKey: ['members', cid], queryFn: () => api.members(cid) });
  const myId = auth.data?.user?.id;
  const [title, setTitle] = useState(t('polls.defaultTitle'));
  const [description, setDescription] = useState('');
  const [locationHint, setLocationHint] = useState('');
  const [dates, setDates] = useState<string[]>([]);
  const [dateInput, setDateInput] = useState(todayISO());
  const [start, setStart] = useState('19:00');
  const [end, setEnd] = useState('');
  const [deadlineDate, setDeadlineDate] = useState('');
  const [deadlineTime, setDeadlineTime] = useState('23:59');
  const [minYes, setMinYes] = useState(4);
  const [maybeCounts, setMaybeCounts] = useState(false);
  const [includeMe, setIncludeMe] = useState(true);
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const [guests, setGuests] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const others = (members.data ?? []).filter((m) => m.userId !== myId);
  // Standard: alle Mitglieder ausgewählt
  const selected = picked ?? new Set(others.map((m) => m.userId));

  const addDate = () => {
    if (dateInput && !dates.includes(dateInput)) setDates([...dates, dateInput].sort());
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (dates.length < 2) return setError(t('polls.minSlots'));
    setBusy(true);
    setError(null);
    try {
      const slots = dates.map((d) => {
        const startAt = zonedToUtc(d, start);
        let endAt: string | null = null;
        if (end) {
          endAt = zonedToUtc(d, end);
          // Ende nach Mitternacht → Folgetag
          if (endAt <= startAt) endAt = new Date(new Date(endAt).getTime() + 86_400_000).toISOString();
        }
        return { startAt, endAt };
      });
      const participants = [
        ...others.filter((m) => selected.has(m.userId)).map((m) => ({ displayName: m.name, userId: m.userId, isRequired: false })),
        ...guests
          .split('\n')
          .map((g) => g.trim())
          .filter(Boolean)
          .map((g) => ({ displayName: g.slice(0, 60), userId: null, isRequired: false })),
      ];
      const created = await api.createPoll(cid, {
        title,
        description,
        locationHint,
        deadline: deadlineDate ? zonedToUtc(deadlineDate, deadlineTime || '23:59') : null,
        minYes,
        maybeCounts,
        slots,
        participants,
        includeMe,
      });
      await qc.invalidateQueries({ queryKey: ['polls', cid] });
      void qc.invalidateQueries({ queryKey: ['schedule', cid] });
      navigate(`/c/${cid}/polls/${created.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card poll-create" onSubmit={submit}>
      <h2>{t('polls.new')}</h2>
      <label>
        {t('polls.name')}
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} required />
      </label>
      <label>
        {t('polls.location')}
        <input value={locationHint} onChange={(e) => setLocationHint(e.target.value)} maxLength={200} />
      </label>
      <label>
        {t('polls.description')}
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} rows={2} />
      </label>

      <h3>{t('polls.slots')}</h3>
      <p className="small muted">{t('polls.slotsHint')}</p>
      <div className="row-form">
        <label>
          {t('polls.dates')}
          <input type="date" value={dateInput} onChange={(e) => setDateInput(e.target.value)} />
        </label>
        <button type="button" onClick={addDate}>
          + {t('polls.addDate')}
        </button>
        <label>
          {t('polls.startTime')}
          <input type="time" value={start} onChange={(e) => setStart(e.target.value)} required />
        </label>
        <label>
          {t('polls.endTime')}
          <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
        </label>
      </div>
      {dates.length > 0 && (
        <div className="poll-dates">
          {dates.map((d) => (
            <span key={d} className="chip on">
              {formatSlot(zonedToUtc(d, start || '00:00'), null)}
              <button type="button" className="chip-x" aria-label={t('common.delete')} onClick={() => setDates(dates.filter((x) => x !== d))}>
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <h3>{t('polls.participants')}</h3>
      <label className="inline">
        <input type="checkbox" checked={includeMe} onChange={(e) => setIncludeMe(e.target.checked)} />
        {t('polls.includeMe')}
      </label>
      {others.length > 0 && (
        <fieldset className="poll-members">
          <legend className="small muted">{t('polls.members')}</legend>
          {others.map((m) => (
            <label key={m.userId} className="inline">
              <input
                type="checkbox"
                checked={selected.has(m.userId)}
                onChange={(e) => {
                  const next = new Set(selected);
                  if (e.target.checked) next.add(m.userId);
                  else next.delete(m.userId);
                  setPicked(next);
                }}
              />
              {m.name}
            </label>
          ))}
        </fieldset>
      )}
      <label>
        {t('polls.guests')}
        <textarea value={guests} onChange={(e) => setGuests(e.target.value)} rows={3} />
      </label>

      <h3>{t('polls.settings')}</h3>
      <div className="row-form">
        <label>
          {t('polls.minYes')} <span className="small">({t('polls.minYesHint')})</span>
          <input type="number" min={0} max={50} value={minYes} onChange={(e) => setMinYes(Number(e.target.value))} />
        </label>
        <label>
          {t('polls.deadline')}
          <input type="date" value={deadlineDate} onChange={(e) => setDeadlineDate(e.target.value)} />
        </label>
        {deadlineDate && (
          <label>
            &nbsp;
            <input type="time" value={deadlineTime} onChange={(e) => setDeadlineTime(e.target.value)} />
          </label>
        )}
      </div>
      <label className="inline">
        <input type="checkbox" checked={maybeCounts} onChange={(e) => setMaybeCounts(e.target.checked)} />
        {t('polls.maybeCounts')}
      </label>

      {error && <div className="error">{error}</div>}
      <div className="modal-actions">
        <button type="button" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button className="primary" disabled={busy}>
          {t('polls.create')}
        </button>
      </div>
    </form>
  );
}
